import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

export const USAGE_HORIZONS = [
  {
    key: 'current_month',
    label: 'Current Month',
    caption: (period) => `${period.period_start} – ${period.anchor_date}`,
  },
  {
    key: 'rolling_90',
    label: 'Rolling 90 Days',
    caption: (period) => `${period.engaged_start} – ${period.anchor_date}`,
  },
  {
    key: 'all_time',
    label: 'All Time',
    caption: (period, tracking) => (
      tracking?.tracking_start
        ? `since ${tracking.tracking_start}`
        : 'no events recorded yet'
    ),
  },
];

/**
 * Per-tool usage table for one of the three reporting horizons.
 *
 * `productUsage` is keyed by horizon: { current_month: { features, totals }, ... }.
 * Set `showNetworks` for the CSGF view, where each user maps onto portfolio networks.
 */
const ProductUsageTable = ({ productUsage, period, usageTracking, showNetworks = false }) => {
  const [horizon, setHorizon] = useState('current_month');
  const block = productUsage?.[horizon] || { features: [], totals: {} };
  const features = block.features || [];
  const totals = block.totals || {};
  const meta = USAGE_HORIZONS.find((item) => item.key === horizon);

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={horizon}
        onChange={(_event, next) => { if (next) setHorizon(next); }}
        sx={{ mb: 1.5 }}
      >
        {USAGE_HORIZONS.map((item) => (
          <ToggleButton key={item.key} value={item.key}>
            {item.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {meta && period && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {meta.caption(period, usageTracking)}
        </Typography>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Tool / Feature</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Users</TableCell>
              {showNetworks && (
                <TableCell align="center" sx={{ fontWeight: 600 }}>Networks</TableCell>
              )}
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Tool-Days
                <Typography variant="caption" display="block" color="text.secondary">
                  distinct user-days
                </Typography>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Actions
                <Typography variant="caption" display="block" color="text.secondary">
                  analyses run
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((usage) => (
              <TableRow key={usage.feature_key} hover>
                <TableCell>{usage.label}</TableCell>
                <TableCell align="center">{(usage.users || 0).toLocaleString()}</TableCell>
                {showNetworks && (
                  <TableCell align="center">{(usage.networks || 0).toLocaleString()}</TableCell>
                )}
                <TableCell align="center">{(usage.tool_days || 0).toLocaleString()}</TableCell>
                <TableCell align="center">{(usage.actions || 0).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>All tools (distinct)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                {(totals.users || 0).toLocaleString()}
              </TableCell>
              {showNetworks && (
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {(totals.networks || 0).toLocaleString()}
                </TableCell>
              )}
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                {(totals.tool_days || 0).toLocaleString()}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                {(totals.actions || 0).toLocaleString()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProductUsageTable;
